import { createSelector } from 'reselect';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import localStorage from '@utils/localStorage';
import createKeyByUserName from '@utils/tempDashboard/createKeyByUserName';
import { setFromToByScrollingMark } from '@utils/scrollingWindowFromTo';
import { tryParse } from '@utils/tryParse';
import { PRESET_DASHBOARD_TYPE } from '@utils/presets/constants';
import defaultTempDashboard from './tempDashboardsStore';
import {
  getActiveTabId,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
} from '../common/selectors';
import { SCOPE, prefix } from './types';
import { isAppModule, USECASE_KEY_WIDGET_DRILLDOWN } from '@src/utils/constants';
import {
  isPresetDashboard,
  isSavedDashboard,
  isSharingDashboard,
  isDrillDownDashboard,
  getSharingIdFromUrl,
  getSavedDashboardIdFromUrl,
  getPresetKeyFromUrl,
  isAiAlertsDiagnosticsDashboard,
  isAppFlowPresetDashboard,
  getAppFlowUsecaseKeyFromUrl,
  isUserTimelineDashboard,
  isAppFrustrationSignalPresetDashboard,
  getAppFrustrationSignalUsecaseKeyFromUrl,
  isAppEventPresetDashboard,
  getAppEventUsecaseKeyFromUrl,
  getAppEventIdFromUsecaseKey,
  getAppFrustrationSignalIdFromUsecaseKey,
  getAppFlowIdFromUsecaseKey,
} from '@src/utils/preset';
import { selectedAiAlertSelector } from '../trends/aiAlerts/selectors';
import { metricKeys } from '@pulse/metrics';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import { ECO_WIDGET_TYPES } from '@src/components/widget-panel/config/eco';
import limitationStore from '@src/globalStore/limitationStore';

const selector = (state) => get(state, SCOPE);

export default selector;

export const afterInitializationSelector = createSelector(
  selector,
  (store) => store.afterInitialization,
);

export const currentActiveDashboard: any = createSelector(
  selector,
  (store) => store.currentActiveDashboard,
);

export const tempKeysSelector = createSelector(selector, (store) =>
  Array.from(store.keys).map((key) => `${key}`),
);

export const savedKeysSelector = createSelector(selector, (store) =>
  Array.from(store.savedRecentlyKeys).map((key) => `${key}`),
);

// sd-note: this gives the list of tabs that show up as pulse tabs for saved and temp dashboards
// recently saved dashboards are sorted here
export const dashboardListSelector: any = createSelector(
  tempKeysSelector,
  savedKeysSelector,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
  (tempKeys, savedKeys, customerId, userName) => {
    const localStorageKey = createKeyByUserName(prefix, customerId, userName);
    let tempDashboards = localStorage.get(localStorageKey) || cloneDeep(defaultTempDashboard);
    // https://jira.corp.conviva.com/browse/IF-2644
    tempDashboards = tryParse(tempDashboards);
    setFromToByScrollingMark(tempDashboards);
    return Object.keys(tempDashboards.dashboards)
      .filter((key) => tempKeys.includes(`${key}`) || savedKeys.includes(key))
      .sort(
        (prevKey, currentKey) =>
          get(tempDashboards, `[${prevKey}].createTime`, 0) -
          get(tempDashboards, `[${currentKey}].createTime`, 0),
      )
      .map((key) => ({
        ...get(tempDashboards, `dashboards[${key}]`, {}),
        key,
      }));
  },
);

export const dashboardsSelector: any = createSelector(
  tempKeysSelector,
  savedKeysSelector,
  selectedCustomerIdSelector,
  selectedUserNameSelector,
  (tempKeys, savedKeys, customerId, userName) => {
    const localStorageKey = createKeyByUserName(prefix, customerId, userName);
    let tempDashboards = localStorage.get(localStorageKey) || cloneDeep(defaultTempDashboard);
    // https://jira.corp.conviva.com/browse/IF-2644
    tempDashboards = tryParse(tempDashboards);
    setFromToByScrollingMark(tempDashboards);
    const dashboardKeys = Object.keys(tempDashboards.dashboards).filter(
      (key) => tempKeys.includes(`${key}`) || savedKeys.includes(key),
    );

    return dashboardKeys.reduce((dashboards, key) => {
      const cloneDashboards = dashboards;
      cloneDashboards[key] = tempDashboards.dashboards[key];
      return cloneDashboards;
    }, {});
  },
);

//DPI preset scope
export const activeDashboardSelector: any = createSelector(
  getActiveTabId,
  currentActiveDashboard,
  dashboardListSelector,
  selectedAiAlertSelector,
  (activeID, currentActiveDashboard, localDashboards, selectedAiAlert) => {
    let key = '';
    let dataScope = '';
    let widgetMeta: any = {};
    const limitations = limitationStore.getState();
    const limitation = limitations[key];

    // TODO: usecase/preset key should be part of page meta

    if (isSavedDashboard()) {
      // saved dashboard
      const savedId = getSavedDashboardIdFromUrl();
      if (String(savedId) === String(currentActiveDashboard?.id)) {
        const { usecaseKey } = get(currentActiveDashboard, 'config', {});
        widgetMeta = get(currentActiveDashboard, 'config.widgetMeta', {});
        key = usecaseKey;
      }
    } else if (isAppFlowPresetDashboard()) {
      key = getAppFlowUsecaseKeyFromUrl();
    } else if (isAppFrustrationSignalPresetDashboard()) {
      key = getAppFrustrationSignalUsecaseKeyFromUrl();
    } else if (isAppEventPresetDashboard()) {
      key = getAppEventUsecaseKeyFromUrl();
    } else if (isPresetDashboard()) {
      // preset
      key = getPresetKeyFromUrl();
    } else if (isSharingDashboard()) {
      // sharing
      const sharingId = getSharingIdFromUrl();
      if (String(sharingId) === String(currentActiveDashboard?.id)) {
        const { usecaseKey } = get(currentActiveDashboard, 'state', {});
        widgetMeta = get(currentActiveDashboard, 'state.widgetMeta', {});
        key = usecaseKey;
      }
    } else if (isDrillDownDashboard()) {
      // preset drilldown
      const drilldownDashboard = (localDashboards as any[]).find(
        (it) => String(it.id) === activeID,
      );
      if (drilldownDashboard) {
        key = get(drilldownDashboard, 'dashboard.usecaseKey');
        widgetMeta = get(drilldownDashboard, 'dashboard.widgetMeta', {});
      }
    }

    if (isAppModule) {
      if (isAiAlertsDiagnosticsDashboard()) {
        const alertMetricKey = get(selectedAiAlert, 'metricName', '');

        if (alertMetricKey === metricKeys.AverageRequestDuration) {
          key = PRESET_DASHBOARD_TYPE.AI_ALERTS_DIAGNOSTICS_AverageRequestDuration;
        }

        const alertMetricMeta = getMetricMetaByKey({ metricKey: alertMetricKey });
        if (alertMetricMeta?.flowID !== null) {
          key = USECASE_KEY_WIDGET_DRILLDOWN;
          widgetMeta = { type: ECO_WIDGET_TYPES.flow, flowId: alertMetricMeta?.flowID };

          if (alertMetricMeta?.flowID) {
            key = `flow:${alertMetricMeta.flowID}`;
          }
        }
      }

      if (isUserTimelineDashboard()) {
        const search = new URLSearchParams(window.location.search);
        const flowId = search.get('flowId');

        if (flowId) {
          key = USECASE_KEY_WIDGET_DRILLDOWN;
          widgetMeta = { type: ECO_WIDGET_TYPES.flow, flowId };
        }
      }

      dataScope = get(limitation, ['dataScope'], '');
    }

    return {
      usecaseKey: key,
      dataScope,
      widgetMeta,
      eventId: getAppEventIdFromUsecaseKey(key) || undefined,
      flowId: getAppFlowIdFromUsecaseKey(key) || undefined,
      frustrationSignalId: getAppFrustrationSignalIdFromUsecaseKey(key) || undefined,
    };
  },
);

export const hasChangedMapSelector = createSelector(dashboardsSelector, (dashboards) =>
  Object.keys(dashboards).reduce((result, key) => {
    const cloneResult = result;
    const dashboard = dashboards[key];
    cloneResult[key] = (dashboard.isSaved && dashboard.hasChanged) || !dashboard.isSaved;
    return cloneResult;
  }, {}),
);

export const needCheckDimensionSelector = createSelector(
  activeDashboardSelector,
  // enable checking for flow preset and trends for eco as well
  (state) => !!state && isAppModule,
);
